/* React base class */
import { Component } from "react"

/* Redux store connection */
import { connect } from "react-redux"

/* Router HOC and shared utilities */
import { withRouter, utils } from "../helpers"

/* Icon component */
import Icon from "../components/Icon"

/* Brand logo */
import Logo from "../components/Logo"

/* Mobile menu drawer */
import Menu from "./Menu"

/* Notifications drawer */
import NotificationsDrawer from "../components/NotificationsDrawer"

/* City selection modal */
import CityModal from "../components/CityModal"

/* Reference API: search suggestions */
import { suggest } from "../api/Ref"

/* i18n */
import { translate } from "../translates"

const RECENT_KEY = "recent-searches"
const POPULAR_FALLBACK = ["Маникюр", "Стрижка", "Массаж", "Окрашивание"]

/* Widget Header */
class Header extends Component {

    constructor(props) {
        super(props)

        /* State */
        this.state = {
            q: "",
            focused: false,
            cityOpen: false,
            mobileOpen: false,
            mSearchOpen: false,
            notifOpen: false,
            searching: false,
            cats: [],
            organizations: []
        }

        /* Search */
        this.search = null
    }

    /* Draw Query */
    _onQuery = (value) => {

        /* Store the input value */
        this.setState({ q: value })

        /* Cancel the pending request */
        if (this.search) {
            clearTimeout(this.search)
        }

        /* Too short — clear results and stop */
        if (value.trim().length < 2) {
            this.setState({ cats: [], organizations: [], searching: false })
            return
        }

        /* Debounced suggest request (300ms after the last keystroke) */
        this.setState({ searching: true })
        this.search = setTimeout(() => {
            const cityId = this.props.city ? this.props.city.id : null
            suggest(value.trim(), cityId).then((response) => {
                if (response && response.status === 200) {
                    this.setState({ cats: response.data.categories || [], organizations: response.data.organizations || [], searching: false })
                }
                else {
                    this.setState({ searching: false })
                }
            })
        }, 300)
    }

    /* Draw Go */
    _go = () => {

        /* Fields */
        const { q } = this.state

        this.setState({ focused: false, mSearchOpen: false })
        const value = q.trim()
        if (value) {
            this._saveRecent(value)
        }
        this.props.navigate(value ? `/search?q=${encodeURIComponent(value)}` : "/search")
    }

    /* Pick a recent or popular query — save it and open search */
    _pick = (query) => {
        this._saveRecent(query)
        this.setState({ focused: false, mSearchOpen: false })
        this.props.navigate(`/search?q=${encodeURIComponent(query)}`)
    }

    /* Open a suggested organization or category — save it to history, then navigate */
    _openResult = (name, path) => {
        this._saveRecent(name)
        this.setState({ focused: false, mSearchOpen: false })
        this.props.navigate(path)
    }

    /* Recent searches from localStorage */
    _readRecent = () => {
        try {
            const raw = localStorage.getItem(RECENT_KEY)
            const list = raw ? JSON.parse(raw) : []
            return Array.isArray(list) ? list.slice(0, 5) : []
        }
        catch {
            return []
        }
    }

    /* Save a query to the top of the recent list (deduped, max 5) */
    _saveRecent = (query) => {
        const value = query.trim()
        if (!value) {
            return
        }
        const list = [value, ...this._readRecent().filter((item) => item.toLowerCase() !== value.toLowerCase())].slice(0, 5)
        localStorage.setItem(RECENT_KEY, JSON.stringify(list))
    }

    /* Popular queries — real categories from the store, with a fallback */
    _popular = () => {
        const { data } = this.props
        const categories = data && Array.isArray(data.categories) ? data.categories : []
        if (categories.length > 0) {
            return categories.slice(0, 6).map((category) => category.name)
        }
        return POPULAR_FALLBACK
    }

    /* Draw Search */
    _search = () => {

        /* Fields */
        const { city } = this.props
        const { q, cityOpen, focused } = this.state

        return (
            <div className="search">
                <span className="search__icon"><Icon name="search" size={18} /></span>
                <input
                    className="search__input"
                    placeholder={translate("Поиск услуг, организаций и мастеров")}
                    value={q}
                    onChange={(event) => this._onQuery(event.target.value)}
                    onFocus={() => this.setState({ focused: true })}
                    onBlur={() => setTimeout(() => this.setState({ focused: false }), 150)}
                    onKeyDown={(event) => { if (event.key === "Enter") this._go() }}
                />
                <div className="search__divider" />
                <button className="search__city" onClick={() => this.setState({ cityOpen: !cityOpen })}>
                    <Icon name="pin" size={16} />
                    {city ? city.name : translate("Город")}
                    <Icon name="chevron_down" size={14} />
                </button>
                <button className="search__btn" onClick={this._go}>{translate("Найти")}</button>

                {focused && this._suggest()}

                {cityOpen && <CityModal onClose={() => this.setState({ cityOpen: false })} />}
            </div>
        )
    }

    /* Draw Suggest */
    _suggest = () => {

        /* Fields */
        const { q, cats, organizations, searching } = this.state
        const query = q.trim()
        const hasResults = cats.length > 0 || organizations.length > 0
        const recent = this._readRecent()
        const popular = this._popular()

        return (
            <div className="suggest">

                {/* Empty query — recent searches and popular queries */}
                {!query && (
                    <div className="suggest__default">
                        {recent.length > 0 && (
                            <div className="suggest__group">
                                <div className="suggest__label">{translate("Недавние")}</div>
                                {recent.map((item) => (
                                    <button key={item} className="suggest__item" onMouseDown={() => this._pick(item)}>
                                        <Icon name="clock" size={16} className="suggest__icon" />
                                        <span>{item}</span>
                                    </button>
                                ))}
                            </div>
                        )}
                        <div className="suggest__group">
                            <div className="suggest__label">{translate("Популярные запросы")}</div>
                            <div className="suggest__chips">
                                {popular.map((item) => (
                                    <button key={item} className="chip" onMouseDown={() => this._pick(item)}>{translate(item)}</button>
                                ))}
                            </div>
                        </div>
                    </div>
                )}
                {/* Searching */}
                {query && searching && (
                    <div className="suggest__loading"><span className="suggest__spinner" /> {translate("Ищем…")}</div>
                )}

                {/* Nothing found */}
                {query && !searching && !hasResults && (
                    <div className="suggest__empty">{translate("Ничего не найдено по запросу")} «{q}»</div>
                )}

                {/* Matching services */}
                {query && cats.length > 0 && (
                    <div className="suggest__group">
                        <div className="suggest__label">{translate("Услуги")}</div>
                        {cats.map((item) => (
                            <button key={`${item.id}`} className="suggest__item" onMouseDown={() => this._openResult(item.name, `/search?category=${item.id}`)}>
                                <span>{translate(item.name)}</span>
                            </button>
                        ))}
                    </div>
                )}
                {/* Matching organizations */}
                {query && organizations.length > 0 && (
                    <div className="suggest__group">
                        <div className="suggest__label">{translate("Организации")}</div>
                        {organizations.map((item) => (
                            <button key={`${item.id}`} className="suggest__item" onMouseDown={() => this._openResult(item.name, `/organization/${item.url || item.id}`)}>
                                <span className="suggest__org">
                                    <span className="suggest__org-name">{item.name}</span>
                                    <span className="suggest__org-addr">{item.addr}</span>
                                </span>
                                <span className="suggest__rating">
                                    <Icon name="star" size={12} fill="var(--star)" stroke="none" />
                                    {item.rating ? Number(item.rating).toFixed(1) : "—"}
                                </span>
                            </button>
                        ))}
                    </div>
                )}
            </div>
        )
    }

    /* Draw Nav */
    _nav = () => {

        /* Fields */
        const { user, lang, favourites } = this.props
        const favCount = Array.isArray(favourites) ? favourites.length : 0

        return (
            <nav className="nav">
                <button className="nav__btn nav__btn--icon nav__msearch" onClick={() => this.setState({ mSearchOpen: true })} aria-label={translate("Поиск")}>
                    <Icon name="search" size={20} />
                </button>
                <div className="lang">
                    <button className={lang === "ru" ? "is-active" : ""} onClick={() => this.props.setLang("ru")}>RU</button>
                    <button className={lang === "kk" ? "is-active" : ""} onClick={() => this.props.setLang("kk")}>KZ</button>
                </div>
                <button className="nav__btn nav__btn--icon" onClick={() => user ? this.props.navigate("/favourites") : this.props.toggleAuth(true)} aria-label={translate("Избранное")}>
                    <Icon name="heart" size={20} />
                    {favCount > 0 && <span className="nav__badge">{favCount}</span>}
                </button>
                <button className="nav__btn nav__btn--icon" onClick={() => this.setState({ notifOpen: true })} aria-label={translate("Уведомления")}>
                    <Icon name="bell" size={20} />
                    <span className="nav__dot" />
                </button>
                {user ? (
                    <>
                        <button className="avatar-btn" onClick={() => this.props.navigate("/profile")}>
                            <span className="avatar"><img src={user.avatar || utils.FALLBACK_AVATAR} alt="" onError={utils.onAvatarError} /></span>
                            <span>{user.name}</span>
                        </button>
                        <button className="nav__btn nav__btn--icon nav__profile-m" onClick={() => this.props.navigate("/account")} aria-label={translate("Профиль")}>
                            <Icon name="user" size={20} />
                        </button>
                    </>
                ) : (
                    <button className="nav__btn nav__login" onClick={() => this.props.toggleAuth(true)}>
                        <Icon name="user" size={18} />
                        <span>{translate("Войти")}</span>
                    </button>
                )}
            </nav>
        )
    }

    /* Draw Mobile */
    _mobile = () => {

        /* Fields */
        const { favourites } = this.props
        const favCount = Array.isArray(favourites) ? favourites.length : 0

        return (
            <div className="header__mobile">
                <button className="burger" onClick={() => this.setState({ mSearchOpen: true })} aria-label={translate("Поиск")}>
                    <Icon name="search" size={20} />
                </button>
                <button className="burger" onClick={() => this.setState({ mobileOpen: true })} aria-label={translate("Меню")}>
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M3 6h18M3 12h18M3 18h18" /></svg>
                    {favCount > 0 && <span className="burger__badge">{favCount}</span>}
                </button>
            </div>
        )
    }

    /* Draw Mobile Search */
    _msearch = () => {

        /* Fields */
        const { mSearchOpen, q, cats, organizations, searching } = this.state
        const query = q.trim()
        const hasResults = cats.length > 0 || organizations.length > 0
        const popular = this._popular()
        const recent = this._readRecent()

        if (!mSearchOpen) {
            return null
        }

        return (
            <div className="msearch-overlay" onClick={() => this.setState({ mSearchOpen: false })}>
                <div className="msearch-sheet" onClick={(event) => event.stopPropagation()}>
                    <div className="msearch-bar">
                        <Icon name="search" size={18} />
                        <input autoFocus value={q} onChange={(event) => this._onQuery(event.target.value)} placeholder={translate("Поиск услуг, организаций и мастеров")} onKeyDown={(event) => { if (event.key === "Enter") this._go() }} />
                        <button onClick={() => this.setState({ mSearchOpen: false })} aria-label={translate("Закрыть")}><Icon name="close" size={20} /></button>
                    </div>
                    <div className="msearch-results">
                        {query && searching && (
                            <div className="suggest__loading"><span className="suggest__spinner" /> {translate("Ищем…")}</div>
                        )}
                        {hasResults && (
                            <div className="msearch-group">
                                {cats.map((item) => (
                                    <button key={`${item.id}`} className="msearch-item" onClick={() => this._openResult(item.name, `/search?category=${item.id}`)}>
                                        <Icon name="search" size={16} /> {translate(item.name)}
                                    </button>
                                ))}
                                {organizations.map((item) => (
                                    <button key={`${item.id}`} className="msearch-item" onClick={() => this._openResult(item.name, `/organization/${item.url || item.id}`)}>
                                        <Icon name="pin" size={16} /> {item.name}
                                    </button>
                                ))}
                            </div>
                        )}
                        {query && !searching && !hasResults && (
                            <div className="suggest__empty">{translate("Ничего не найдено по запросу")} «{q}»</div>
                        )}
                        {!query && recent.length > 0 && (
                            <div className="msearch-group">
                                <div className="msearch-label">{translate("Недавние")}</div>
                                {recent.map((item) => (
                                    <button key={item} className="msearch-item" onClick={() => this._pick(item)}>
                                        <Icon name="clock" size={16} /> {item}
                                    </button>
                                ))}
                            </div>
                        )}
                        {!query && (
                            <div className="msearch-group">
                                <div className="msearch-label">{translate("Популярное")}</div>
                                <div className="msearch-chips">
                                    {popular.map((item) => (
                                        <button key={item} onClick={() => this._pick(item)}>{translate(item)}</button>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        )
    }

    render = () => {

        /* Fields */
        const { mobileOpen, notifOpen } = this.state

        return (
            <header className="header">
                <div className="container header__inner">
                    <a href="/" onClick={(event) => { event.preventDefault(); this.props.navigate("/") }} aria-label="Bookify">
                        <Logo />
                    </a>
                    {this._search()}
                    {this._nav()}
                    {this._mobile()}
                </div>

                {this._msearch()}

                {mobileOpen && <Menu onClose={() => this.setState({ mobileOpen: false })} />}
                {notifOpen && (
                    <NotificationsDrawer navigate={this.props.navigate} onClose={() => this.setState({ notifOpen: false })} />
                )}
            </header>
        )
    }
}

/* Map State To Props */
const mapStateToProps = state => {
    return {
        user: state.user,
        city: state.city,
        data: state.data,
        lang: state.lang,
        favourites: state.favourites
    }
}

/* Map Dispatch To Props */
const mapDispatchToProps = dispatch => {
    return {
        toggleAuth: data => dispatch({ type: "TOGGLE_AUTH", payload: data }),
        setLang: data => dispatch({ type: "SET_LANG", payload: data })
    }
}

/* Export */
export default connect(mapStateToProps, mapDispatchToProps)(withRouter(Header))
